import {
  Change,
  EditCommittedEventArguments,
  EditStatus,
  SharedTree,
  SharedTreeEvent,
  Transaction,
  TreeView,
} from '@fluid-experimental/tree';
import { DataObject } from '@fluidframework/aqueduct';
import { IFluidHandle } from '@fluidframework/core-interfaces';
import { ISharedDirectory } from '@fluidframework/map';
import { ConsensusRegisterCollection } from '@fluidframework/register-collection';
import { ActivityEventType, MfsStatusCode } from '@metaos/mfs-telemetry';
import { ulid } from 'ulid';
import {
  AppDataOf,
  AppDataOfCollection,
  DestinationPosition,
  ElementId,
  isMfsItem,
  MfsCollection,
  MfsCollectionEntry,
  MfsCollectionId,
  MfsCollectionItem,
  MfsCollectionQuery,
  MfsDataStore,
  MfsDeltaValue,
  MfsEvents,
  MfsHost,
  MfsItem,
  MfsItemAndRelationship,
  MfsItemId,
  MfsQuery,
  MfsRelationship,
  MfsSubCollectionId,
  MfsValue,
  RelationshipKind,
  SourcePosition,
} from '..';
import {
  assertPrecondition,
  mfsError,
  validateCollectionId,
  validateInsertItem,
  validateInsertItems,
  validateItemId,
  validatePatchItem,
  validatePropertyOperation,
  validateSetItemProperty,
} from '../errors';
import { assert, logActivity, logVerbose, logVerboseDecorator, logWarn } from '../log';
import {
  MfsCollectionInternal,
  omitCollectionValues,
  throwIfInvalidCollectionQuery,
  values,
} from '../store/collections';
import { canUseLabelId, LabelTracker, RESET_LABEL_ID } from '../store/labels';
import { canCreateItem, ensureNotCollection } from '../store/mfsCreateRules';
import { MfsItemTypeGuard, MfsRelationshipQuery } from '../store/mfsTypes';
import { skip, take, throwIfInvalidQuery } from '../store/query';
import { validateRelatedItemsBatchOperation, validateRelatedItemsOperation } from '../store/relations';
import { Writable } from '../utility';
import { MfsFluidLabelTracker } from './runtime';
import { deleteRootKey, getRootKey, setRootKey } from './runtime/rootDirectory';
import * as tree from './snapshot';
import { getMfsChangeDelta } from './snapshot/deltas';

export const ITEM_LABELS_KEY = 'itemLabelsTracker';
export const ITEMS_TREE_KEY = 'itemsTree';

/** A Fluid {@link DataObject} implementation of {@link MfsDataStore} interface. */
export class MfsFluidDataStore extends DataObject implements MfsDataStore {
  items!: SharedTree;
  labelTracker!: MfsFluidLabelTracker;
  app!: MfsHost;
  podId!: string;

  /** @inheritdoc */
  setHost(app: MfsHost): void {
    this.app = app;
  }

  /** @inheritdoc */
  getHost(): MfsHost {
    return this.app;
  }

  /** @inheritdoc */
  setId(id: string): void {
    this.podId = id;
  }

  /** @inheritdoc */
  getId(): string {
    return this.podId;
  }

  /** The current immutable view of the items tree. */
  private get view(): TreeView {
    return this.items.currentView;
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.SINGLE)
  async createItem<T extends MfsItem>(item: AppDataOf<T>): Promise<T> {
    const [transaction, view] = this.createTransaction();
    assert(() => ensureNotCollection(item));
    const [itemWithId, changes] = await this.createItemInternal(view, item, ulid() as MfsItemId);
    logVerbose('createItem', `Creating item`, { itemId: itemWithId.id });
    this.commitChanges(changes, transaction);
    return itemWithId;
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.SINGLE)
  async createCollection<T extends MfsCollection>(
    collection: AppDataOfCollection<T>,
    itemIds?: (MfsItemId | MfsCollectionId)[]
  ): Promise<T> {
    const [transaction, view] = this.createTransaction();
    const itemToCreate: MfsCollectionInternal<T> = { ...collection, [values]: itemIds ?? [] };
    const [internalCollection, changes] = await this.createItemInternal(view, itemToCreate, ulid() as MfsItemId);
    logVerbose('createCollection', `Creating collection`, {
      collectionId: internalCollection.id,
      kind: internalCollection.kind,
      itemIds: itemIds,
    });
    this.commitChanges(changes, transaction);
    return omitCollectionValues(internalCollection);
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.NONE)
  getMfsItem(itemId: MfsItemId, select?: string[]): MfsItem | undefined {
    logVerbose('getMfsItem', `Getting mfs item`, { itemId });
    return tree.getMfsItem(this.items, this.view, itemId, select);
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.NONE)
  getItemsByLabel<T extends MfsItem>(
    typeGuard: MfsItemTypeGuard<T>,
    label: string,
    labelId?: string
  ): IterableIterator<T> {
    if (labelId) {
      logVerbose('getItemsByLabel', 'Getting items by labelId');
      return this.getItemByLabelId(typeGuard, label, labelId);
    }
    logVerbose('getItemsByLabel', `LabelId not specified, getting items by label`);
    return this.getItems(typeGuard, {
      filter: (item) => item.label === label && (!labelId || item.labelId === labelId),
    });
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.NONE)
  getItem<T extends MfsItem>(
    itemId: MfsItemId,
    typeGuard: (item: MfsItem) => item is T,
    select?: string[]
  ): T | undefined {
    logVerbose('getItem', `Getting item`, { itemId });
    return tree.getItem(this.items, this.view, itemId, typeGuard, select);
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.NONE)
  getItems<T extends MfsItem>(typeGuard: (item: MfsItem) => item is T, query?: MfsQuery): IterableIterator<T> {
    throwIfInvalidQuery(query);
    let results = tree.getItemsWithoutPaging(this.view, typeGuard, query);
    logVerbose('getItems', 'Filtering result with the query', {
      top: query?.top,
      skip: query?.skip,
      orderBy: query?.orderBy,
    });
    if (!query) {
      logVerbose('getItems', 'Query not specified, returning results without paging');
      return results;
    }

    if (query.skip) {
      results = skip(results, query.skip);
    }

    if (query.top) {
      results = take(results, query.top);
    }

    return results;
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.SINGLE)
  @validateItemId
  async deleteItem(itemId: MfsItemId): Promise<void> {
    const [transaction, view] = this.createTransaction();

    if (!tree.itemExists(this.items, view, itemId)) {
      logSkipped('deleteItem', itemId);
      return;
    }
    logVerbose('deleteItem', `Deleting item`, { itemId });
    const changes = tree.deleteMfsItem(this.items, view, itemId);
    await this.stopLabelTracking(view, itemId);
    this.commitChanges(changes, transaction);
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.SINGLE)
  @validateItemId
  async deleteItemByLabelId(label: string, labelId: string): Promise<void> {
    const itemId = this.labelTracker.getItemId(label, labelId);
    if (!itemId) {
      logVerbose('deleteItemByLabelId', `ItemId not found on deleteItemByLabelId, skipping`);
      return;
    }
    await this.labelTracker.untrack(label, labelId);
    logVerbose('deleteItemByLabelId', `stopTracking `, { itemId });
    const maybeItem = this.getItem<MfsItem>(itemId, isMfsItem);
    if (maybeItem) {
      const [transaction, view] = this.createTransaction();
      logVerbose('deleteItemByLabelId', `Deleting item`, { itemId });
      const changes = tree.deleteMfsItem(this.items, view, maybeItem.id);
      if (changes) {
        logVerbose(`deleteItemByLabelId`, `Commiting changes`, { itemId });
        this.commitChanges(changes, transaction);
      }
    }
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.SINGLE)
  deleteItems(): Promise<void> {
    const [transaction, view] = this.createTransaction();
    const changes = tree.deleteAllMfsItems(view);
    this.commitChanges(changes, transaction);
    return this.labelTracker.cleanUp();
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.SINGLE)
  @validateItemId
  @validatePatchItem
  async patchItem<T extends MfsItem>(itemId: MfsItemId, patch: Partial<AppDataOf<T>>): Promise<void> {
    // need to save the state prior to the change
    const rollbackItemState = this.getMfsItem(itemId);
    const [transaction, view] = this.createTransaction();
    const changes = tree.updateMfsItem(this.items, view, itemId, patch);
    if (changes.length === 0) {
      logSkipped('patchItem', itemId);
      return;
    }
    logVerbose('patchItem', `Patching item`, { itemId });
    await this.restartLabelTracking(view, itemId, LabelTracker.tryGetLabelIdFromPatch(patch));
    try {
      this.commitChanges(changes, transaction);
    } catch (error) {
      logVerbose('patchItem', 'Error on commitChanges');

      if (rollbackItemState) {
        await this.restartLabelTracking(view, itemId, rollbackItemState.labelId ?? RESET_LABEL_ID);
      }
      throw error;
    }
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.SINGLE)
  @validateSetItemProperty
  async setItemProperty(
    itemId: MfsItemId,
    propertyName: string,
    propertyValue: MfsValue | MfsDeltaValue
  ): Promise<void> {
    const rollbackItemState = this.getMfsItem(itemId);
    const [transaction, view] = this.createTransaction();
    const changes = tree.setMfsItemProperty(this.items, view, itemId, propertyName, propertyValue);
    if (changes.length === 0) {
      logSkipped('setItemProperty', itemId);
      return;
    }
    logVerbose('setItemProperty', `Setting item property`, { itemId });
    await this.restartLabelTracking(view, itemId, LabelTracker.tryGetLabelId(propertyName, propertyValue));
    try {
      this.commitChanges(changes, transaction);
    } catch (error) {
      logVerbose('setItemProperty', 'Error on commitChanges');

      if (rollbackItemState) {
        await this.restartLabelTracking(view, itemId, rollbackItemState.labelId ?? RESET_LABEL_ID);
      }
      throw error;
    }
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.SINGLE)
  @validatePropertyOperation
  async deleteItemProperty(itemId: MfsItemId, propertyName: string): Promise<void> {
    const [transaction, view] = this.createTransaction();
    const change = tree.deleteMfsItemProperty(this.items, view, itemId, propertyName);
    if (!change) {
      logSkipped('deleteItemProperty', itemId);
      return;
    }
    logVerbose('deleteItemProperty', `Deleting item property`, { itemId });
    await this.stopLabelTracking(view, itemId);
    this.commitChanges([change], transaction);
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.NONE)
  @validatePropertyOperation
  getItemProperty(itemId: MfsItemId, propertyName: string): MfsValue | undefined {
    logVerbose('getItemProperty', `Getting item property`, { itemId });
    return tree.tryGetItemProperty(this.items, this.view, itemId, propertyName);
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.SINGLE)
  @validateRelatedItemsOperation
  addRelatedItem(itemId: MfsItemId, relationship: MfsRelationship, ...relatedItemIds: MfsItemId[]): void {
    return this.addRelatedItems(itemId, relationship, relatedItemIds);
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.SINGLE)
  @validateRelatedItemsBatchOperation
  addRelatedItems(itemId: MfsItemId, relationship: MfsRelationship, relatedItemIds: MfsItemId[]): void {
    const [transaction, view] = this.createTransaction();
    const changes =
      relationship.kind === RelationshipKind.TwoWay
        ? tree.addTwoWayRelation(this.items, view, itemId, relationship, relatedItemIds, this.app)
        : tree.addOneWayRelation(this.items, view, itemId, relationship, relatedItemIds, this.app);
    logVerbose('addRelatedItems', `Adding related items`, {
      itemId,
      relationship: { kind: relationship.kind, direction: relationship.direction },
      relatedItemIds,
    });
    this.commitChanges(changes, transaction);
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.SINGLE)
  @validateRelatedItemsOperation
  removeRelatedItem(itemId: MfsItemId, relationship: MfsRelationship, ...relatedItemIds: MfsItemId[]): void {
    return this.removeRelatedItems(itemId, relationship, relatedItemIds);
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.SINGLE)
  @validateRelatedItemsBatchOperation
  removeRelatedItems(itemId: MfsItemId, relationship: MfsRelationship, relatedItemIds: MfsItemId[]): void {
    const [transaction, view] = this.createTransaction();
    const changes =
      relationship.kind === RelationshipKind.TwoWay
        ? tree.removeTwoWayRelation(this.items, view, itemId, relationship, relatedItemIds, this.app)
        : tree.removeOneWayRelation(this.items, view, itemId, relationship, relatedItemIds, this.app);
    logVerbose('removeRelatedItems', `removing related items`, {
      itemId,
      relationship: { kind: relationship.kind, direction: relationship.direction },
      relatedItemIds,
    });
    this.commitChanges(changes, transaction);
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.NONE)
  getRelatedItems(itemId: MfsItemId, relationship: MfsRelationship): IterableIterator<MfsItem> {
    const view = this.view;
    const { kind, direction } = relationship;
    logVerbose('getRelatedItems', `getting related items`, { itemId, relationship: { kind, direction } });
    return tree.getRelatedItems(this.items, view, this.app, itemId, relationship);
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.NONE)
  *getAllRelationships(itemId: MfsItemId, query?: MfsRelationshipQuery): IterableIterator<MfsRelationship> {
    const view = this.view;
    logVerbose('getAllRelationships', `getting all relationships`, { itemId });
    for (const { relationship } of tree.getAllRelationships(this.items, view, itemId, query)) {
      yield relationship;
    }
  }

  /** @inheritdoc */
  @logVerboseDecorator()
  @validateItemId
  *getAllRelatedItemsAndRelationships(
    itemId: MfsItemId,
    query?: MfsRelationshipQuery
  ): IterableIterator<MfsItemAndRelationship> {
    const view = this.view;
    logVerbose('getAllRelatedItemsAndRelationships', `getting all related items and relationships`, { itemId });
    for (const { relatedItemId, relationship } of tree.getAllRelationships(this.items, view, itemId, query)) {
      const item = this.getItem(relatedItemId, isMfsItem);
      if (item) {
        yield { item, relationship };
      }
    }
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.NONE)
  getCollectionItems<T extends MfsCollectionItem>(
    collectionId: MfsCollectionId | MfsSubCollectionId,
    query?: MfsCollectionQuery
  ): IterableIterator<T> {
    throwIfInvalidCollectionQuery(query);

    const view = this.view;
    const nodeId = tree.getCollectionNodeId(this.items, view, collectionId);

    logVerbose('getCollectionItems', `Filtering result with query`, {
      collectionId,
      query: {
        top: query?.top,
        skip: query?.skip,
        depth: query?.depth,
        reverse: query?.reverse,
      },
    });

    let results = tree.getCollectionItemsWithoutPaging(
      this.items,
      view,
      nodeId,
      query?.depth ?? 1,
      query?.reverse ?? false,
      query?.select
    );

    if (!query) {
      return results as IterableIterator<T>;
    }

    if (query.skip) {
      results = skip(results, query.skip);
    }

    if (query.top) {
      results = take(results, query.top);
    }

    return results as IterableIterator<T>;
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.NONE)
  getItemIdAt(collectionId: MfsCollectionId, position: SourcePosition): MfsItemId | undefined {
    const view = this.view;
    const itemId = tree.getCollectionElementAt(this.items, view, collectionId, position);
    logVerbose('getItemIdAt', 'Getting item at collection position', { collectionId, position });
    return tree.itemExists(this.items, view, itemId) ? itemId : undefined;
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.SINGLE)
  @validateInsertItems
  insertItems(
    collectionId: MfsCollectionId,
    itemIds: MfsItemId[],
    position?: DestinationPosition
  ): [MfsItemId, ElementId][] {
    const [transaction, view] = this.createTransaction();
    const { changes, itemNodeIds } = tree.insertToCollection(this.items, view, collectionId, itemIds, position);
    logVerbose('insertItems', `Inserting items on collection position`, { collectionId, itemIds, position });
    this.commitChanges(changes, transaction);
    return itemNodeIds;
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.SINGLE)
  @validateInsertItem
  insertItem(collectionId: MfsCollectionId, itemId: MfsItemId, position?: DestinationPosition): [MfsItemId, ElementId] {
    return this.insertItems(collectionId, [itemId], position)[0];
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.SINGLE)
  @validateItemId
  removeItemAt(collectionId: MfsCollectionId, at: SourcePosition): void {
    const [transaction, view] = this.createTransaction();
    const change = tree.removeFromCollection(this.items, view, collectionId, at);
    logVerbose('removeItemAt', `removing items at a collection position`, { collectionId, at });
    if (!change) {
      // removing already removed element is a no-op.
      logWarn(`removeItemAt`, MfsStatusCode.NotFound, {
        message: `not found, elementId:${at.elementId} in collectionId: ${collectionId}`,
      });
      return;
    }

    this.commitChanges([change], transaction);
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.SINGLE)
  @validateCollectionId
  removeItemById(collectionId: MfsCollectionId | MfsSubCollectionId, itemId: MfsItemId): void {
    const [transaction, view] = this.createTransaction();
    logVerbose('removeItemById', `Removing item by id`, { collectionId, itemId });
    if (!itemId) {
      logSkipped(`removeItemById`, itemId);
      return;
    }

    const id = tree.getCollectionNodeId(this.items, view, collectionId);
    const changes = tree.removeItemById(this.items, view, id, itemId);
    this.commitChanges(changes, transaction);
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.SINGLE)
  removeAllItems(collectionId: MfsCollectionId | MfsSubCollectionId): void {
    const [transaction, view] = this.createTransaction();
    const id = tree.getCollectionNodeId(this.items, view, collectionId);
    const changes = tree.clearCollection(this.items, view, id);
    logVerbose('removeAllItems', `Removing all items from collection`, { collectionId });
    this.commitChanges(changes, transaction);
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.SINGLE)
  moveItem(
    collectionId: MfsCollectionId,
    from: SourcePosition,
    to: DestinationPosition,
    destinationCollectionId?: MfsCollectionId
  ): void {
    const [transaction, view] = this.createTransaction();
    const changes = tree.moveCollectionItem(this.items, view, collectionId, from, to, destinationCollectionId);
    logVerbose('moveItem', `Moving item`, { collectionId, from, to, destinationCollectionId });
    this.commitChanges(changes, transaction);
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.NONE)
  getElementIdsOf(
    collectionId: MfsCollectionId | MfsSubCollectionId,
    itemId: MfsItemId,
    depth?: number
  ): IterableIterator<MfsCollectionEntry> {
    const view = this.view;
    const collectionNodeId = tree.getCollectionNodeId(this.items, view, collectionId);
    logVerbose('getElementIdsOf', `Getting elementIds`, { collectionId, itemId, depth });
    return tree.getEntriesOf(this.items, view, itemId, collectionNodeId, depth);
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.NONE)
  getElementIds(
    collectionId: MfsCollectionId | MfsSubCollectionId,
    depth?: number
  ): IterableIterator<MfsCollectionEntry> {
    const view = this.view;
    const collectionNodeId = tree.getCollectionNodeId(this.items, view, collectionId);
    logVerbose('getElementIds', `Getting elementIds`, { collectionId, depth });
    return tree.getEntriesOf(this.items, view, undefined, collectionNodeId, depth);
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.NONE)
  hasElement(collectionId: MfsCollectionId | MfsSubCollectionId, itemId: MfsItemId, depth?: number): boolean {
    const view = this.view;
    logVerbose('hasElement', `Checking if collection has element`, { collectionId, itemId, depth });
    // don't expose already deleted items.
    if (!tree.itemExists(this.items, view, itemId)) {
      logVerbose('hasElement', `Item ${itemId} does not exist in tree`, { collectionId, itemId });
      return false;
    }
    const collectionNodeId = tree.getCollectionNodeId(this.items, view, collectionId);
    return tree.hasElement(this.items, view, itemId, collectionNodeId, depth);
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.NONE)
  getMfsItemKey(propertyName: string): string {
    return propertyName;
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.SINGLE)
  setBootItem(key: string, value: MfsItemId): void {
    setRootKey(this.root, key, value);
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.NONE)
  getBootItem(key: string): MfsItemId | undefined {
    return getRootKey(this.root, key);
  }

  /** @inheritdoc */
  @logActivity(ActivityEventType.SINGLE)
  deleteBootItem(key: string): void {
    return deleteRootKey(this.root, key);
  }

  /** @inheritdoc */
  protected async initializingFirstTime(): Promise<void> {
    logVerbose('initializingFirstTime', 'Initializing the data store for the first time');
    this.createItemsTree(ITEMS_TREE_KEY);
    this.createLabelsRegistry(ITEM_LABELS_KEY);
  }

  /** @inheritdoc */
  protected async hasInitialized(): Promise<void> {
    logVerbose('hasInitialized', 'Running hasInitialized method');
    await Promise.all([this.loadItemsTree(), this.loadLabelsRegistry()]);
    this.registerAudience();
    this.registerTreeListeners();
    this.registerRootListeners();
  }

  /** @protected */
  protected emitMfsEvent<E extends keyof MfsEvents>(event: E, arg: MfsEvents[E]): void {
    logVerbose('emitMfsEvent', 'Emitting', { event, arg });
    this.emit(event, arg);
  }

  /** @private */
  private createItemsTree(key: string): void {
    const tree = SharedTree.create(this.runtime);
    this.root.set(key, tree.handle);
    logVerbose('createItemsTree', `Created SharedTree`, { key });
  }

  /** @private */
  private createLabelsRegistry(key: string): void {
    const crc = ConsensusRegisterCollection.create<string>(this.runtime);
    this.root.set(key, crc.handle);
    logVerbose('createLabelsRegistry', `Created ConsensusRegisterCollection`, { key });
  }

  /** @private */
  private async loadItemsTree(): Promise<void> {
    const tree = await this.root.get<IFluidHandle<SharedTree>>(ITEMS_TREE_KEY)?.get();
    assertPrecondition(!!tree);
    this.items = tree;
  }

  /** @private */
  private async loadLabelsRegistry(): Promise<void> {
    const crc = await this.root.get<IFluidHandle<ConsensusRegisterCollection<MfsItemId>>>(ITEM_LABELS_KEY)?.get();
    assertPrecondition(!!crc);
    this.labelTracker = new MfsFluidLabelTracker(crc);
  }

  /** @private */
  private registerAudience(): void {
    const audience = this.runtime.getAudience();
    audience.on('addMember', (clientId: string) => {
      this.emitMfsEvent('audienceChanged', { clientId: clientId, status: 'add' });
    });
    audience.on('removeMember', (clientId: string) => {
      this.emitMfsEvent('audienceChanged', { clientId: clientId, status: 'remove' });
    });
  }

  /** @private */
  private registerTreeListeners(): void {
    this.items.on(SharedTreeEvent.EditCommitted, ({ editId, local, tree }: EditCommittedEventArguments) => {
      const editIndex = tree.edits.getIndexOfId(editId);
      const previousView = tree.logViewer.getRevisionViewInSession(editIndex);

      const mfsChanges = getMfsChangeDelta(tree.currentView, previousView);

      if (!mfsChanges.isOK) {
        logWarn('unrecognizedChangeSequence', MfsStatusCode.BadRequest, {
          error: mfsError.unrecognizedChangeSequence(`editId: ${editId}`),
        });
        this.emitMfsEvent('staleDataDetected', null);
        return;
      }

      for (const mfsChange of mfsChanges.value) {
        const { kind, ...args } = mfsChange;
        this.emitMfsEvent(kind, { ...args, isLocal: local });
      }
    });
  }

  /** @private */
  private registerRootListeners(): void {
    this.root.on('valueChanged', ({ key, previousValue }, isLocal: boolean, target: ISharedDirectory) => {
      const currentValue = target.get<MfsItemId>(key);
      const changeKind = currentValue !== undefined ? (previousValue === undefined ? 'add' : 'update') : 'remove';
      // only raise events for boot items of the current host.
      this.emitMfsEvent('bootItemsChanged', { isLocal, key: key, changeKind, previousValue, currentValue });
    });
  }

  /** @private */
  private createTransaction(): [Transaction, TreeView] {
    const transaction = new Transaction(this.items);
    const view = transaction.startingView;
    return [transaction, view];
  }

  /** @private */
  private commitChanges(changes: readonly Change[], transaction: Transaction): void {
    if (changes.length === 0) {
      logVerbose('commitChanges', `No changes to commit`);
      return;
    }

    const status = transaction.apply(changes as Writable<Change[]>);
    logVerbose('commitChanges', `edit status: ${status}`);

    if (status !== EditStatus.Applied) {
      throw mfsError.droppedEdit(status, changes);
    }

    logVerbose('commitChanges', `Commiting changes`);
    transaction.closeAndCommit();
    logVerbose('commitChanges', `transaction committed`);
  }

  /** Creates the given item and registers change event listeners. */
  private async createItemInternal<T extends MfsItem>(
    view: TreeView,
    item: AppDataOf<T>,
    itemId: MfsItemId
  ): Promise<[T, Change[]]> {
    assert(() => canCreateItem(item, itemId, this.labelTracker));
    //http://github.com/microsoft/TypeScript/issues/35858
    const itemWithId: T = { id: itemId, ...item } as unknown as T;
    await this.startLabelTracking(itemWithId);
    logVerbose('createItemInternal', `Creating item`, { itemId: itemWithId.id });
    const changes = tree.insertMfsItem(this.items, view, itemWithId);
    return [itemWithId, changes];
  }

  /** Claims the labelId for the given item, if any. */
  private startLabelTracking<T extends MfsItem>(item: T): Promise<void> {
    const labelProps = LabelTracker.tryGetLabelAndId(item);
    logVerbose('startLabelTracking', `startTracking`, { itemId: item.id });
    return labelProps ? this.labelTracker.track(labelProps.label, labelProps.labelId, item.id) : Promise.resolve();
  }

  /** Checks if there's any label-id to update and it is allowed to be updated. */
  private async restartLabelTracking(view: TreeView, itemId: MfsItemId, newLabelId?: string): Promise<void> {
    if (newLabelId || newLabelId === RESET_LABEL_ID) {
      const { label, labelId } = tree.getLabelProps(this.items, view, itemId);

      logVerbose('restartLabelTracking', `restartTracking`, { itemId });

      if (newLabelId === labelId) {
        logVerbose('restartLabelTracking', `newLabelId is equal to old labelId, skipping restartLabelTracking`, {
          itemId,
        });
        return;
      }

      if (newLabelId !== RESET_LABEL_ID) {
        const result = canUseLabelId(this.labelTracker, label, newLabelId, itemId);
        logVerbose('restartLabelTracking', `newLabelId is different from RESET_LABEL_ID`, { itemId });
        if (result.isOK === false) {
          throw result.error;
        }

        if (result.value !== itemId) {
          await this.labelTracker.track(label, newLabelId, itemId);
        } else {
          logWarn('restartLabelTracking', MfsStatusCode.NotAllowed, {
            message: `newLabelId already claimed by current itemId: ${itemId}`,
          });
        }
      }

      if (labelId) {
        logVerbose('restartLabelTracking', `LabelId exists, untracking`, { itemId });
        await this.labelTracker.untrack(label, labelId);
      }
    }
  }

  /** Releases the labelId claimed by the item with given `itemId`, if any. */
  private async stopLabelTracking(view: TreeView, itemId: MfsItemId): Promise<void> {
    const { label, labelId } = tree.getLabelProps(this.items, view, itemId);
    logVerbose('stopLabelTracking', `starting stopTracking`, { itemId });
    if (labelId) {
      logVerbose('stopLabelTracking', `Untracking label`, { itemId });
      return this.labelTracker.untrack(label, labelId);
    } else {
      logVerbose('stopLabelTracking', `LabelId not found, skipping`);
      return Promise.resolve();
    }
  }

  /** @yields The MfsItem that's associated with the given label+labelId, if any. */
  private *getItemByLabelId<T extends MfsItem>(
    typeGuard: MfsItemTypeGuard<T>,
    label: string,
    labelId: string
  ): IterableIterator<T> {
    const itemId = this.labelTracker.getItemId(label, labelId);
    const maybeItem = itemId ? this.getItem<T>(itemId, typeGuard) : undefined;
    if (maybeItem) {
      yield maybeItem;
    }
  }
}

const logSkipped = (method: string, itemId: MfsItemId) =>
  logWarn(method, MfsStatusCode.NotFound, { message: `skipped, no change itemId: ${itemId}.` });
